import React, { useEffect } from 'react';

interface SimpleModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/* 旧 styled-components 定義をクラス文字列定数として保持。
 * font-size は line-height 未指定だったため arbitrary value を使う。 */
const OVERLAY =
  'fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center ' +
  'z-[1000] p-5 max-[640px]:items-start max-[640px]:p-3';

/* 旧 Container。`.product-registration-modal` を外から className で渡す
 * 呼び出し側があるため、旧 `&.product-registration-modal ...` 入れ子は
 * arbitrary variant でそのまま再現する（詳細度も 0,2,x のまま）。 */
const CONTAINER_BASE =
  'bg-white rounded-tasky-sm w-full max-h-[min(90vh,calc(100dvh-40px))] flex flex-col ' +
  'shadow-[0_10px_25px_rgba(0,0,0,0.3)] ' +
  'max-[640px]:max-w-[calc(100vw-24px)] max-[640px]:max-h-[calc(100dvh-24px)] ' +
  '[&.product-registration-modal]:overflow-hidden ' +
  '[&.product-registration-modal]:border [&.product-registration-modal]:border-warm-border ' +
  '[&.product-registration-modal]:rounded-tasky-md [&.product-registration-modal]:bg-[#F8F7F3] ' +
  '[&.product-registration-modal]:shadow-[0_18px_42px_rgba(31,25,39,0.24)] ' +
  '[&.product-registration-modal>div:first-child]:min-h-[58px] ' +
  '[&.product-registration-modal>div:first-child]:py-0 ' +
  '[&.product-registration-modal>div:first-child]:px-6 ' +
  '[&.product-registration-modal>div:first-child]:border-b ' +
  '[&.product-registration-modal>div:first-child]:border-b-warm-border ' +
  '[&.product-registration-modal>div:first-child]:bg-[#F8F7F3] ' +
  '[&.product-registration-modal>div:first-child_h2]:text-dark ' +
  '[&.product-registration-modal>div:first-child_h2]:text-[18px] ' +
  '[&.product-registration-modal>div:first-child_h2]:font-extrabold ' +
  '[&.product-registration-modal>div:first-child_button]:inline-grid ' +
  '[&.product-registration-modal>div:first-child_button]:place-items-center ' +
  '[&.product-registration-modal>div:first-child_button]:w-[34px] ' +
  '[&.product-registration-modal>div:first-child_button]:h-[34px] ' +
  '[&.product-registration-modal>div:first-child_button]:p-0 ' +
  '[&.product-registration-modal>div:first-child_button]:rounded-[999px] ' +
  '[&.product-registration-modal>div:first-child_button]:text-[#6B7280] ' +
  '[&.product-registration-modal>div:first-child_button]:leading-none ' +
  '[&.product-registration-modal>div:first-child_button:hover]:bg-warm-hover ' +
  '[&.product-registration-modal>div:first-child_button:hover]:text-dark ' +
  '[&.product-registration-modal>div:last-child]:p-0 ' +
  '[&.product-registration-modal>div:last-child]:bg-[#F8F7F3]';

const CONTAINER_SIZE: Record<NonNullable<SimpleModalProps['size']>, string> = {
  sm: 'max-w-[500px]',
  md: 'max-w-[700px]',
  lg: 'max-w-[900px]',
  xl: 'max-w-[1200px]',
};

const HEADER =
  'flex justify-between items-center px-6 py-5 border-b border-b-[#e0e0e0] shrink-0 ' +
  'bg-[#fafafa] rounded-t-tasky-sm max-[640px]:px-4 max-[640px]:py-3.5';

const TITLE =
  'm-0 text-[1.3rem] font-semibold text-[#333] min-w-0 overflow-hidden text-ellipsis ' +
  'whitespace-nowrap max-[640px]:text-[1rem]';

const CLOSE_BUTTON =
  'bg-transparent border-0 text-[24px] cursor-pointer text-[#666] px-2 py-1 rounded ' +
  'transition-all duration-200 ease-[ease] hover:text-black hover:bg-[#eee]';

const CONTENT =
  'flex-1 min-h-0 overflow-y-auto p-6 max-[640px]:p-4 ' +
  '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-[#f1f1f1] ' +
  '[&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-thumb]:rounded ' +
  '[&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] ' +
  '[scrollbar-width:thin] [scrollbar-color:#c1c1c1_#f1f1f1]';

const SimpleModal: React.FC<SimpleModalProps> = ({ isOpen, onClose, title, children, size = 'md', className }) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={OVERLAY} onClick={onClose}>
      <div
        className={[CONTAINER_BASE, CONTAINER_SIZE[size] ?? CONTAINER_SIZE.md, className]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={HEADER}>
          <h2 className={TITLE}>{title}</h2>
          <button className={CLOSE_BUTTON} onClick={onClose}>&times;</button>
        </div>
        <div className={CONTENT}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default SimpleModal;
